import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import {
  getOverviewStatistics,
  getAttemptsTimeline,
  getScoreDistribution,
  getStatisticsBySubject,
  getStatisticsBreakdown,
  getAcademicYearStatistics,
} from "../../../services/StatisticsService";
import { useYearFilters, YearFilters } from "./YearFilters";
import { useTranslation } from "react-i18next";
import { formatDuration, getLocale } from "../../../utils/i18nLabels";
import AcademicYearStatistics from "./AcademicYearStatistics";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);


const KpiCard = ({ title, value, icon, color, subtitle }) => (
  <div className="col-xl-3 col-sm-6 mb-3">
    <div className="card h-100">
      <div className="card-body d-flex align-items-center justify-content-between">
        <div>
          <p className="mb-1 text-muted">{title}</p>
          <h3 className="mb-0">{value ?? "-"}</h3>
          {subtitle && <small className="text-muted">{subtitle}</small>}
        </div>
        <span className={`avatar avatar-lg bg-${color}-transparent rounded-circle d-flex align-items-center justify-content-center`}>
          <i className={`${icon} text-${color} fs-24`}></i>
        </span>
      </div>
    </div>
  </div>
);

const Statistics = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [distribution, setDistribution] = useState([]);
  const [bySubject, setBySubject] = useState([]);
  const [breakdownBy, setBreakdownBy] = useState("class");
  const [breakdown, setBreakdown] = useState([]);
  const [yearStats, setYearStats] = useState(null);
  const filters = useYearFilters();

  // Un seul jeu de filtres pour toute la page : tout est recalculé quand l'un change.
  useEffect(() => {
    if (!filters.ready) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError("");
      const p = filters.paramsWithYear;
      try {
        const [overviewRes, timelineRes, distributionRes, subjectRes, breakdownRes, yearRes] = await Promise.all([
          getOverviewStatistics(p),
          getAttemptsTimeline(14, p),
          getScoreDistribution(p),
          getStatisticsBySubject(p),
          getStatisticsBreakdown(breakdownBy, p),
          filters.yearId ? getAcademicYearStatistics(filters.yearId, filters.params) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setOverview(overviewRes.data?.data);
        setTimeline(timelineRes.data?.data?.series || []);
        setDistribution(distributionRes.data?.data || []);
        setBySubject(subjectRes.data?.data || []);
        setBreakdown(breakdownRes.data?.data || []);
        setYearStats(yearRes?.data?.data || null);
      } catch (err) {
        if (!cancelled) setLoadError(err?.response?.data?.message || t("common.ui.impossibleChargerStatistiques"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.ready, filters.key, breakdownBy]);

  if (!overview && loading) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t("common.crud.loading")}</span>
          </div>
        </div>
      </div>
    );
  }

  if (loadError && !overview) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-warning">{loadError}</div>
        </div>
      </div>
    );
  }

  const timelineData = {
    labels: timeline.map((t) => new Date(t.date).toLocaleDateString(getLocale(), { day: "2-digit", month: "2-digit" })),
    datasets: [
      {
        label: t("pages.statistics.attemptsStartedLabel"),
        data: timeline.map((t) => t.count),
        backgroundColor: "#6366f1",
        borderRadius: 4,
      },
    ],
  };

  const examStatusData = {
    labels: [t("common.examStatus.draft"), t("common.examStatus.validated"), t("common.examStatus.published"), t("common.examStatus.archived")],
    datasets: [
      {
        data: [overview.exams.draft, overview.exams.validated || 0, overview.exams.published, overview.exams.archived],
        backgroundColor: ["#94a3b8", "#0ea5e9", "#22c55e", "#f59e0b"],
      },
    ],
  };

  // Les six états de l'étudiant pour l'année universitaire sélectionnée.
  const STATE_KEYS = [
    ["admitted", "decision_admitted", "#22c55e"],
    ["admittedWithCredits", "decision_admitted_with_credits", "#0ea5e9"],
    ["repeating", "decision_repeating", "#f59e0b"],
    ["graduated", "decision_graduated", "#6366f1"],
    ["excluded", "decision_excluded", "#ef4444"],
    ["undecided", "studentsUndecided", "#94a3b8"],
  ];
  const statesTotal = yearStats ? STATE_KEYS.reduce((sum, [k]) => sum + (yearStats[k] || 0), 0) : 0;
  const statesData = {
    labels: STATE_KEYS.map(([, label]) => t(`pages.statistics.${label}`)),
    datasets: [
      {
        data: STATE_KEYS.map(([k]) => (yearStats ? yearStats[k] || 0 : 0)),
        backgroundColor: STATE_KEYS.map(([, , color]) => color),
      },
    ],
  };

  const distributionData = {
    labels: distribution.map((d) => `${d.bucket}%`),
    datasets: [
      {
        label: t("pages.statistics.resultCountLabel"),
        data: distribution.map((d) => d.count),
        backgroundColor: "#0ea5e9",
        borderRadius: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("menu.statistics")}</li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body py-3">
            <YearFilters filters={filters} />
          </div>
        </div>
        {loadError && <div className="alert alert-warning">{loadError}</div>}

        <div className="row">
          <KpiCard title={t("menu.students.title")} value={overview.students} icon="ti ti-school" color="primary" />
          {overview.teachers !== null ? (
            <KpiCard title={t("menu.teachers.title")} value={overview.teachers} icon="ti ti-user-check" color="info" />
          ) : (
            <KpiCard
              title={t("pages.filters.averageOutOf20")}
              value={overview.results.averageOutOf20 != null ? `${overview.results.averageOutOf20} / 20` : "-"}
              icon="ti ti-target-arrow"
              color="info"
            />
          )}
          <KpiCard
            title={t("common.ui.examensPublies")}
            value={overview.exams.published}
            icon="ti ti-clipboard-text"
            color="success"
            subtitle={t("pages.statistics.kpiTotalSuffix", { count: overview.exams.total })}
          />
          <KpiCard
            title={t("pages.statistics.kpiGlobalPassRate")}
            value={overview.results.passRate !== null ? `${overview.results.passRate}%` : "-"}
            icon="ti ti-chart-pie"
            color="warning"
            subtitle={t("pages.statistics.kpiResultsSuffix", { count: overview.results.total })}
          />
        </div>

        <div className="row">
          <KpiCard
            title={t("common.ui.scoreMoyen")}
            value={overview.results.averageScore !== null ? `${overview.results.averageScore}%` : "-"}
            icon="ti ti-target-arrow"
            color="primary"
          />
          <KpiCard
            title={t("pages.statistics.kpiAverageTime")}
            value={formatDuration(overview.averageTimeSpentSeconds)}
            icon="ti ti-clock"
            color="info"
          />
          <KpiCard
            title={t("common.ui.sessionsCours")}
            value={overview.sessions.ongoing}
            icon="ti ti-broadcast"
            color="success"
            subtitle={t("pages.statistics.kpiScheduledSuffix", { count: overview.sessions.scheduled })}
          />
          <KpiCard
            title={t("pages.statistics.kpiOngoingAttempts")}
            value={overview.attempts.in_progress}
            icon="ti ti-player-play"
            color="warning"
            subtitle={t("pages.statistics.kpiTotalSuffix", { count: overview.attempts.total })}
          />
        </div>

        <div className="row">
          <div className="col-lg-8 mb-3">
            <div className="card h-100">
              <div className="card-header">
                <h5 className="mb-0">{t("pages.statistics.attemptsTimelineTitle")}</h5>
              </div>
              <div className="card-body">
                <Bar data={timelineData} options={chartOptions} height={90} />
              </div>
            </div>
          </div>
          <div className="col-lg-4 mb-3">
            <div className="card h-100">
              <div className="card-header">
                <h5 className="mb-0">{t("pages.statistics.studentStatesTitle")}</h5>
              </div>
              <div className="card-body d-flex align-items-center justify-content-center">
                {statesTotal > 0 ? (
                  <Doughnut data={statesData} />
                ) : (
                  <p className="text-muted mb-0">{t("pages.statistics.noDataAvailable")}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-4 mb-3">
            <div className="card h-100">
              <div className="card-header">
                <h5 className="mb-0">{t("pages.statistics.examsByStatusTitle")}</h5>
              </div>
              <div className="card-body d-flex align-items-center justify-content-center">
                {overview.exams.total > 0 ? (
                  <Doughnut data={examStatusData} />
                ) : (
                  <p className="text-muted mb-0">{t("pages.statistics.noExamsYet")}</p>
                )}
              </div>
            </div>
          </div>
          <div className="col-lg-8 mb-3">
            <div className="card h-100">
              <div className="card-header">
                <h5 className="mb-0">{t("pages.statistics.scoreDistributionTitle")}</h5>
              </div>
              <div className="card-body">
                <Bar data={distributionData} options={chartOptions} height={90} />
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12 mb-3">
            <div className="card h-100">
              <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                <h5 className="mb-0">{t("pages.filters.breakdownTitle")}</h5>
                <select className="form-select form-select-sm w-auto" value={breakdownBy} onChange={(e) => setBreakdownBy(e.target.value)}>
                  {["class", "department", "level", "exam"].map((k) => (
                    <option key={k} value={k}>{t(`pages.filters.by.${k}`)}</option>
                  ))}
                </select>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th>{t(`pages.filters.by.${breakdownBy}`)}</th>
                        <th>{t("menu.students.title")}</th>
                        <th>{t("menu.results.results")}</th>
                        <th>{t("pages.filters.averageOutOf20")}</th>
                        <th>{t("common.ui.tauxReussite")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakdown.length ? breakdown.map((row) => (
                        <tr key={row.key}>
                          <td className="fw-medium">{row.label}</td>
                          <td>{row.students}</td>
                          <td>{row.results}</td>
                          <td>{row.averageOutOf20 ?? "-"}</td>
                          <td>
                            {row.passRate !== null ? (
                              <span className={`badge badge-soft-${row.passRate >= 50 ? "success" : "danger"}`}>{row.passRate}%</span>
                            ) : "-"}
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan="5" className="text-center py-4 text-muted">{t("pages.statistics.noDataAvailable")}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">{t("pages.statistics.bySubjectTitle")}</h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table">
                <thead className="thead-light">
                  <tr>
                    <th>{t("common.fields.subject")}</th>
                    <th>{t("menu.exams.title")}</th>
                    <th>{t("menu.results.results")}</th>
                    <th>{t("pages.filters.averageOutOf20")}</th>
                    <th>{t("common.ui.tauxReussite")}</th>
                  </tr>
                </thead>
                <tbody>
                  {bySubject.length > 0 ? (
                    bySubject.map((row) => (
                      <tr key={row.subjectId}>
                        <td className="fw-medium">{row.subjectName}</td>
                        <td>{row.examCount}</td>
                        <td>{row.resultCount}</td>
                        <td>{row.averageOutOf20 ?? "-"}</td>
                        <td>
                          {row.passRate !== null ? (
                            <span className={`badge badge-soft-${row.passRate >= 50 ? "success" : "danger"}`}>
                              {row.passRate}%
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-4 text-muted">
                        {t("pages.statistics.noDataAvailable")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <AcademicYearStatistics filters={filters} />
    </div>
  );
};

export default Statistics;